import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
// DISABLED (2026-09-28): only the synthetic-ASP save input used InputText.
// import { InputText } from '@allygpo/ally-ux';
import { AppTable, AppTableHorizontalScrollControls, Button, Dropdown, InputNumber, MultiSelect, ProgressSpinner, useAppTableHorizontalScroll } from '@allygpo/ally-ux';
import { getCoreRowModel, useReactTable, type ColumnDef, type Table } from '@tanstack/react-table';
import { Checkbox, PageHeader, alignEnd, headerEnd, multiSelectSearch, tableView } from '../../components/ui';

import ApiService, { handleApiError } from '../../services/api';
import { scenarioActions, useScenarioContext } from '../../context/ScenarioContext';
import { FeeSchedule, ProductComparisonRow, ProductFamilySelection } from '../../types';
import { money, pct } from '../../utils/format';
import './ProductComparison.css';

type ReimbursementBasis = 'medicare' | 'payer' | 'manual' | 'asp';
type TierAchievement = 'earned' | 'on_track' | 'manual';

/** A product row with the what-if economics for the current reimbursement basis. */
type WhatIfRow = ProductComparisonRow & {
  effectiveReimbursement: number;
  payerRateFound: boolean;
  tierAchievement: TierAchievement;
  appliedRebate: number;
  whatIfNcr: number | null;
  whatIfBefore: number | null;
};

/** What the product economics cells read from `meta.view`. */
interface EconomicsView {
  selectedIds: Set<string>;
  toggle: (id: string) => void;
  basis: ReimbursementBasis;
}

/**
 * The table with its scroll controls. Its own component so the scroll hook runs when the table mounts
 * (the page renders it only after the economics load).
 */
const EconomicsTable: React.FC<{ table: Table<WhatIfRow> }> = ({ table }) => {
  const horizontalScroll = useAppTableHorizontalScroll();
  return (
    <AppTable
      isBordered={false}
      ariaLabel="Product economics"
      table={table}
      size="sm"
      className="pc-table"
      horizontalScroll={horizontalScroll}
      headerSlot={<AppTableHorizontalScrollControls horizontalScroll={horizontalScroll} />}
    />
  );
};

const economicsColumns: ColumnDef<WhatIfRow>[] = [
  {
    id: 'use',
    size: 56,
    header: 'Use',
    cell: ({ row: { original: row }, table }) => {
      const { selectedIds, toggle } = tableView<EconomicsView, WhatIfRow>(table);
      return <Checkbox checked={selectedIds.has(row.product_family_id)} onChange={() => toggle(row.product_family_id)} ariaLabel={`Use ${row.product_family_name}`} />;
    },
  },
  {
    id: 'family',
    size: 230,
    header: 'Product family',
    cell: ({ row: { original: row } }) => <>
      <strong>{row.product_family_name}</strong>
      {!row.has_volume && <small>{row.never_ordered ? 'New to this practice — select to include' : 'No invoice volume'}</small>}
    </>,
  },
  { id: 'cta', size: 130, header: 'CTA', cell: ({ row: { original: row } }) => row.category },
  { id: 'mix', size: 110, header: () => headerEnd('Current mix'), meta: alignEnd, cell: ({ row: { original: row } }) => pct(row.current_distribution) },
  { id: 'price', size: 110, header: () => headerEnd('Net price'), meta: alignEnd, cell: ({ row: { original: row } }) => (row.has_volume ? money(row.net_price_per_eu) : '—') },
  { id: 'medicare', size: 130, header: () => headerEnd('Medicare reimb.'), meta: alignEnd, cell: ({ row: { original: row } }) => money(row.medicare_reimbursement_per_eu) },
  {
    id: 'effective',
    size: 140,
    header: () => headerEnd('Effective reimb.'),
    meta: alignEnd,
    cell: ({ row: { original: row }, table }) => <>
      {row.has_volume ? money(row.effectiveReimbursement) : '—'}
      {tableView<EconomicsView, WhatIfRow>(table).basis === 'payer' && !row.payerRateFound && <small>Medicare fallback</small>}
    </>,
  },
  {
    id: 'rebate',
    size: 190,
    header: () => headerEnd('Rebate assumption'),
    meta: alignEnd,
    cell: ({ row: { original: row } }) => (row.tierAchievement === 'earned'
      ? `Earned PTD · ${money(row.appliedRebate)}`
      : `${row.tierAchievement === 'on_track' ? 'On track' : 'Manual'} · ${money(row.appliedRebate)}`),
  },
  {
    id: 'ncr',
    size: 110,
    header: () => headerEnd('NCR'),
    meta: alignEnd,
    cell: ({ row: { original: row } }) => <span className={row.is_negative ? 'negative' : ''}>{row.has_volume ? money(row.ncr_per_eu) : '—'}</span>,
  },
  {
    id: 'whatif',
    size: 130,
    header: () => headerEnd('What-if NCR'),
    meta: { ...alignEnd, cellClassName: 'whatif', headerClassName: 'whatif' },
    cell: ({ row: { original: row } }) => (
      <span className={row.whatIfNcr !== null && row.whatIfNcr < 0 ? 'negative' : ''}>{row.has_volume ? money(row.whatIfNcr) : '—'}</span>
    ),
  },
  {
    id: 'fee',
    size: 110,
    header: 'Fee schedule',
    cell: ({ row: { original: row } }) => (row.fee_schedule_status === 'complete' ? 'Full' : row.fee_schedule_status === 'partial' ? 'Partial' : 'None'),
  },
];


/**
 * A focused pre-analysis workbench. It deliberately changes no ScenarioIQ
 * assumptions until the user clicks "Use in scenario".
 */
const ProductComparison: React.FC = () => {
  const navigate = useNavigate();
  const { state, dispatch } = useScenarioContext();
  const [account, setAccount] = useState(state.generatorState.form?.logged_in_user ?? '');
  const [categories, setCategories] = useState<string[]>([]);
  // `category` is write-only while the synthetic-ASP save is disabled — that call was
  // its only reader. Kept (rather than collapsed to `[, setCategory]`) so re-enabling
  // is a pure uncomment; the three setCategory call sites are still live.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [category, setCategory] = useState<string>('');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [rows, setRows] = useState<ProductComparisonRow[]>([]);
  const [currentMixNcr, setCurrentMixNcr] = useState<number | null>(null);
  const [unpriced, setUnpriced] = useState<string[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [basis, setBasis] = useState<ReimbursementBasis>('medicare');
  const [feeSchedules, setFeeSchedules] = useState<FeeSchedule[]>([]);
  const [payer, setPayer] = useState('');
  const [manualPercent, setManualPercent] = useState(100);
  const [aspMarkupPercent, setAspMarkupPercent] = useState(10);
  // DISABLED (2026-09-28): the synthetic-ASP *save* flow. The "ASP + markup" basis
  // itself is untouched — it is a local what-if that needs no backend. Only persisting
  // it as a payer schedule is gone, superseded by the published-limit multiplier on the
  // Fee Schedules modal. Backend route POST /fee-schedules/{user}/synthetic-asp is
  // commented out to match, so leaving the button live would 404.
  // const [syntheticPayerName, setSyntheticPayerName] = useState('ASP assumption');
  // const [savingSynthetic, setSavingSynthetic] = useState(false);
  const [mix, setMix] = useState<Record<string, number>>({});
  const [tierAchievement, setTierAchievement] = useState<Record<string, TierAchievement>>({});
  const [manualTierRebate, setManualTierRebate] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => setAccount(state.generatorState.form?.logged_in_user ?? ''), [state.generatorState.form?.logged_in_user]);

  useEffect(() => {
    if (!account) {
      setCategories([]);
      setCategory('');
      setSelectedCategories([]);
      setRows([]);
      return;
    }
    let active = true;
    ApiService.getUserCategories(account)
      .then((response) => {
        if (!active) return;
        const next = response.data?.categories ?? [];
        setCategories(next);
        setCategory((current) => current && next.includes(current) ? current : next[0] ?? '');
        setSelectedCategories((current) => current.length ? current.filter((item) => next.includes(item)) : (next[0] ? [next[0]] : []));
      })
      .catch((requestError) => active && setError(handleApiError(requestError)));
    return () => { active = false; };
  }, [account]);

  useEffect(() => {
    if (!account) { setFeeSchedules([]); setPayer(''); return; }
    let active = true;
    ApiService.getFeeSchedules(account)
      .then((response) => {
        if (!active) return;
        const schedules = response.fee_schedules ?? [];
        setFeeSchedules(schedules);
        const availablePayers = Array.from(new Set(schedules.flatMap((schedule) => Object.keys(schedule.reimbursements ?? {})))).sort();
        setPayer((current) => availablePayers.includes(current) ? current : availablePayers[0] ?? '');
      })
      .catch(() => active && setFeeSchedules([]));
    return () => { active = false; };
  }, [account]);

  const loadComparison = useCallback(() => {
    if (!account || selectedCategories.length === 0) {
      setRows([]);
      setSelectedIds(new Set());
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    Promise.all(selectedCategories.map((selectedCategory) => ApiService.getProductComparison(account, selectedCategory, controller.signal)))
      .then((responses) => {
        // A product family can be mapped to more than one CTA. Keep one decision
        // row for it in a cross-CTA set, while retaining the source CTA for the
        // later hand-off into ScenarioIQ.
        const byProduct = new Map<string, ProductComparisonRow>();
        responses.flatMap((response) => response.rows ?? []).forEach((row) => {
          if (!byProduct.has(row.product_family_id)) byProduct.set(row.product_family_id, row);
        });
        const nextRows = Array.from(byProduct.values());
        setRows(nextRows);
        const pricedRows = nextRows.filter((row) => row.ncr_per_eu !== null && row.category_units > 0);
        const units = pricedRows.reduce((total, row) => total + row.category_units, 0);
        setCurrentMixNcr(units ? pricedRows.reduce((total, row) => total + Number(row.ncr_per_eu) * row.category_units, 0) / units : null);
        setUnpriced(responses.flatMap((response) => response.unpriced_product_families ?? []));
        // Default to the products in the decision set; the user can suppress any.
        // New-to-practice products are candidates, not an assumption. Keep
        // them visible but require the user to explicitly select them.
        setSelectedIds(new Set(nextRows.filter((row) => row.has_volume).map((row) => row.product_family_id)));
        setMix(Object.fromEntries(nextRows.map((row) => [row.product_family_id, row.current_distribution * 100])));
        setTierAchievement(Object.fromEntries(nextRows.map((row) => [row.product_family_id, 'earned'])) as Record<string, TierAchievement>);
        setManualTierRebate({});
      })
      .catch((requestError) => {
        if (!controller.signal.aborted) {
          setError(`${handleApiError(requestError)} Product economics can take up to 90 seconds; retry after confirming the companion server has data access.`);
        }
      })
      .finally(() => !controller.signal.aborted && setLoading(false));
    return () => controller.abort();
  }, [account, selectedCategories]);

  useEffect(() => {
    const cancel = loadComparison();
    return cancel;
  }, [loadComparison]);

  const payerOptions = useMemo(() => Array.from(new Set(feeSchedules.flatMap((schedule) => Object.keys(schedule.reimbursements ?? {})))).sort(), [feeSchedules]);
  const payerRateByProduct = useMemo(() => new Map(feeSchedules.map((schedule) => [String(schedule.product_family_id), Number(schedule.reimbursements?.[payer])])), [feeSchedules, payer]);
  const whatIfRows = useMemo<WhatIfRow[]>(() => rows.map((row) => {
    const medicare = Number(row.medicare_reimbursement_per_eu ?? 0);
    const storedPayerRate = payerRateByProduct.get(row.product_family_id);
    const effectiveReimbursement = basis === 'payer' && Number.isFinite(storedPayerRate)
      ? Number(storedPayerRate)
      : basis === 'manual'
        ? medicare * Math.max(0, manualPercent) / 100
        // Medicare commonly reimburses ASP plus 6%; explicitly expose this
        // transparent synthetic estimate when a complete payer schedule is absent.
        : basis === 'asp'
          ? (medicare / 1.06) * (1 + Math.max(-100, aspMarkupPercent) / 100)
          : medicare;
    const reimbursementDelta = effectiveReimbursement - medicare;
    const achievement = tierAchievement[row.product_family_id] ?? 'earned';
    const earnedRebate = Number(row.rebate_per_eu ?? 0);
    const appliedRebate = achievement === 'earned' ? earnedRebate : Math.max(0, manualTierRebate[row.product_family_id] ?? earnedRebate);
    const ncrBeforeRebate = row.ncr_before_rebate_per_eu === null ? null : Number(row.ncr_before_rebate_per_eu);
    const ncr = ncrBeforeRebate === null ? (row.ncr_per_eu === null ? null : Number(row.ncr_per_eu) + reimbursementDelta + appliedRebate - earnedRebate) : ncrBeforeRebate + appliedRebate + reimbursementDelta;
    return { ...row, effectiveReimbursement, payerRateFound: basis !== 'payer' || Number.isFinite(storedPayerRate), tierAchievement: achievement, appliedRebate, whatIfNcr: ncr, whatIfBefore: ncrBeforeRebate === null ? null : ncrBeforeRebate + reimbursementDelta };
  }), [rows, basis, payerRateByProduct, manualPercent, aspMarkupPercent, tierAchievement, manualTierRebate]);

  const selectedRows = useMemo(() => whatIfRows.filter((row) => selectedIds.has(row.product_family_id) && row.has_volume && row.whatIfNcr !== null), [whatIfRows, selectedIds]);
  // Include newly selected, never-ordered products in the mix editor too. They
  // are intentionally excluded from the NCR math until their price/rebate data
  // is available, but their proposed share must still transfer to the scenario.
  const mixRows = useMemo(() => whatIfRows.filter((row) => selectedIds.has(row.product_family_id)), [whatIfRows, selectedIds]);
  const whatIfNcr = useMemo(() => {
    if (selectedRows.length === 0) return null;
    const totalMix = selectedRows.reduce((sum, row) => sum + Math.max(0, mix[row.product_family_id] ?? 0), 0);
    if (totalMix === 0) return selectedRows.reduce((sum, row) => sum + Number(row.whatIfNcr), 0) / selectedRows.length;
    return selectedRows.reduce((sum, row) => sum + Number(row.whatIfNcr) * Math.max(0, mix[row.product_family_id] ?? 0), 0) / totalMix;
  }, [selectedRows, mix]);

  const toggle = (id: string): void => setSelectedIds((current) => {
    const next = new Set(current);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  // DISABLED (2026-09-28) — see the note on the synthetic state above.
  // const saveSyntheticSchedule = async (): Promise<void> => {
  //   if (!account || !syntheticPayerName.trim()) return;
  //   setSavingSynthetic(true);
  //   setError(null);
  //   try {
  //     await ApiService.createSyntheticAspFeeSchedule(account, syntheticPayerName.trim(), aspMarkupPercent, category);
  //     const response = await ApiService.getFeeSchedules(account);
  //     setFeeSchedules(response.fee_schedules ?? []);
  //     setPayer(syntheticPayerName.trim());
  //     setBasis('payer');
  //   } catch (requestError) {
  //     setError(handleApiError(requestError));
  //   } finally {
  //     setSavingSynthetic(false);
  //   }
  // };

  const useInScenario = (): void => {
    if (!account || selectedIds.size === 0) return;
    const selectedFamilies: Record<string, ProductFamilySelection> = {};
    const selectedRows = rows.filter((row) => selectedIds.has(row.product_family_id));
    const proposedMixByCategory = selectedRows.reduce<Record<string, Record<string, number>>>((all, row) => {
      const inCategory = selectedRows.filter((candidate) => candidate.category === row.category);
      const rawTotal = inCategory.reduce((total, candidate) => total + Math.max(0, mix[candidate.product_family_id] ?? 0), 0);
      all[row.category] = all[row.category] || {};
      all[row.category][row.product_family_id] = rawTotal > 0 ? Math.max(0, mix[row.product_family_id] ?? 0) / rawTotal : 1 / inCategory.length;
      return all;
    }, {});
    rows.forEach((row) => {
      selectedFamilies[row.product_family_name] = {
        include: selectedIds.has(row.product_family_id),
        minDistribution: 0,
        proposedDistribution: proposedMixByCategory[row.category]?.[row.product_family_id] ?? 0,
      };
    });
    dispatch(scenarioActions.setGeneratorState({
      form: {
        ...state.generatorState.form,
        logged_in_user: account,
        // Keep any other CTA work already staged so this comparison can join a
        // multi-area run instead of resetting it to one area.
        categories: Array.from(new Set([...(state.generatorState.form?.categories ?? []), ...selectedRows.map((row) => row.category)])),
        proposed_product_mix_by_category: {
          ...(state.generatorState.form?.proposed_product_mix_by_category ?? {}),
          ...proposedMixByCategory,
        },
        // A mix carried over from here is meant to be modeled, so turn it on for those areas.
        proposed_mix_enabled_by_category: {
          ...(state.generatorState.form?.proposed_mix_enabled_by_category ?? {}),
          ...Object.fromEntries(Object.keys(proposedMixByCategory).map((category) => [category, true])),
        },
      },
      selectedFamilies: { ...state.generatorState.selectedFamilies, ...selectedFamilies },
    }));
    navigate('/generate');
  };

  const economicsTable = useReactTable({
    data: whatIfRows,
    columns: economicsColumns,
    meta: { view: { selectedIds, toggle, basis } satisfies EconomicsView },
    state: { columnPinning: { left: ['use', 'family'] } },
    getRowId: (row) => row.product_family_id,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="flex flex-column u-gap-4">
      <PageHeader title="Product comparison" subtitle="Build a product decision, then test its NCR. Explore reimbursement and product-mix assumptions before creating a ScenarioIQ run." />

      <ol className="pc-workflow" aria-label="Product Comparison workflow">
        <li className="done"><b>1</b><span><strong>Select products</strong><small>Build the decision set.</small></span></li>
        <li className={selectedIds.size ? 'active' : ''}><b>2</b><span><strong>Set assumptions</strong><small>Choose reimbursement and mix.</small></span></li>
        <li className={basis !== 'medicare' || selectedIds.size > 1 ? 'active' : ''}><b>3</b><span><strong>What-if NCR</strong><small>Review the calculated impact.</small></span></li>
        <li><b>4</b><span><strong>Create scenario</strong><small>Carry confirmed products into a run.</small></span></li>
      </ol>

      <section className="pc-controls" aria-label="Comparison selection">
        <div className="pc-practice-context"><span>Practice</span><strong>{state.generatorState.userName || account || 'Select a practice on Home'}</strong></div>
        <label>Therapeutic areas
          <MultiSelect className="pc-area-select" value={selectedCategories} options={categories.map((value) => ({ label: value, value }))} onChange={(event) => {
            const next = Array.isArray(event.value) ? event.value : [];
            setSelectedCategories(next);
            setCategory(next[0] ?? '');
          }} disabled={!account} placeholder="Select one or more areas" display="chip" filter filterTemplate={multiSelectSearch('Search areas')} />
        </label>
      </section>

      {loading && <div className="pc-loading"><ProgressSpinner style={{ width: 32, height: 32 }} /><span>Calculating current product economics…</span></div>}
      {error && <div className="pc-error"><strong>Product comparison could not load.</strong> {error}<Button variant="primary-outline" onClick={() => loadComparison()}>Try again</Button></div>}

      {!loading && !error && rows.length > 0 && (
        <div className="pc-layout">
          <section className="pc-table-card">
            <div className="pc-card-heading"><div><span className="pc-eyebrow">Step 1 · decision set</span><h2>Product economics</h2></div><span>{selectedIds.size} of {rows.length} products selected</span></div>
            <EconomicsTable table={economicsTable} />
            {unpriced.length > 0 && <p className="pc-muted">No economics returned for: {unpriced.join(', ')}.</p>}
          </section>

          <aside className="pc-workbench">
            <div><span className="pc-eyebrow">Step 2 · reimbursement</span><h2>Assumptions</h2></div>
            <div className="pc-toggle"><Button variant={basis === 'medicare' ? 'primary' : 'primary-outline'} onClick={() => setBasis('medicare')}>Medicare</Button><Button variant={basis === 'payer' ? 'primary' : 'primary-outline'} onClick={() => setBasis('payer')} disabled={!payerOptions.length}>Configured payer</Button><Button variant={basis === 'manual' ? 'primary' : 'primary-outline'} onClick={() => setBasis('manual')}>% of Medicare</Button><Button variant={basis === 'asp' ? 'primary' : 'primary-outline'} onClick={() => setBasis('asp')}>ASP + markup</Button></div>
            {basis === 'payer' && <label>Configured payer<Dropdown value={payer} options={payerOptions.map((value) => ({ label: value, value }))} onChange={(event) => setPayer(String(event.value ?? ''))} placeholder="Select payer" /></label>}
            {basis === 'manual' && <label>Reimbursement rate<InputNumber value={manualPercent} suffix="% of Medicare" min={0} max={300} maxFractionDigits={1} onValueChange={(event) => setManualPercent(Math.max(0, Number(event.value ?? 0)))} /></label>}
            {basis === 'asp' && <><label>ASP markup<InputNumber value={aspMarkupPercent} prefix="ASP + " suffix="%" min={-100} max={300} maxFractionDigits={1} onValueChange={(event) => setAspMarkupPercent(Number(event.value ?? 0))} /></label>{/* DISABLED (2026-09-28): saving the ASP assumption as a payer schedule — see the note on the synthetic state above. <label>Save as payer schedule<InputText value={syntheticPayerName} onChange={(event) => setSyntheticPayerName(event.target.value)} placeholder="e.g. UNH ASP + 10%" /></label><Button variant="primary-outline" onClick={saveSyntheticSchedule} loading={savingSynthetic} disabled={!syntheticPayerName.trim()}><i className="pi pi-save" aria-hidden="true" />Save synthetic schedule</Button> */}</>}
            <p className="pc-muted">{basis === 'medicare' ? 'Source: Medicare reimbursement and rebates earned at today’s volume.' : basis === 'payer' ? `Source: configured ${payer || 'payer'} fee schedule. Missing product rates fall back to Medicare.` : basis === 'asp' ? 'Synthetic estimate: current Medicare reimbursement ÷ 1.06, then adjusted by the stated ASP markup. Price and earned rebates remain fixed.' : 'Manual exploratory estimate: reimbursement changes; price and earned rebates remain fixed.'}</p>
            <hr />
            <div><span className="pc-eyebrow">Step 3 · rebate achievement</span><h2>Tier assumptions</h2></div>
            {mixRows.map((row) => {
              const achievement = tierAchievement[row.product_family_id] ?? 'earned';
              return <div className="pc-tier-row" key={`tier-${row.product_family_id}`}><span><strong>{row.product_family_name}</strong><small>{row.category}</small></span><Dropdown value={achievement} options={[{ label: 'Earned PTD', value: 'earned' }, { label: 'On track', value: 'on_track' }, { label: 'Manual tier', value: 'manual' }]} onChange={(event) => setTierAchievement((current) => ({ ...current, [row.product_family_id]: event.value as TierAchievement }))} />{achievement !== 'earned' && <label className="pc-tier-value">Expected rebate / EU<InputNumber value={manualTierRebate[row.product_family_id] ?? Number(row.rebate_per_eu ?? 0)} mode="currency" currency="USD" locale="en-US" min={0} maxFractionDigits={2} onValueChange={(event) => setManualTierRebate((current) => ({ ...current, [row.product_family_id]: Math.max(0, Number(event.value ?? 0)) }))} /></label>}</div>;
            })}
            <p className="pc-muted">Earned PTD uses the current rebate. For On track or Manual tier, enter the rebate expected per equivalent unit. Contract-tier choices will replace this input once the Quarterly NCR snapshot is available.</p>
            <hr />
            <div><span className="pc-eyebrow">Step 4 · what-if mix</span><h2>NCR sensitivity</h2></div>
            {mixRows.map((row) => <label className="pc-mix-row" key={row.product_family_id}><span>{row.product_family_name}{row.never_ordered ? <small className="pc-unpriced">New to practice — NCR pending pricing</small> : null}</span><InputNumber value={mix[row.product_family_id] ?? 0} suffix="%" min={0} max={100} maxFractionDigits={1} onValueChange={(event) => setMix((current) => ({ ...current, [row.product_family_id]: Math.max(0, Number(event.value ?? 0)) }))} /></label>)}
            <div className="pc-result"><span className="pc-eyebrow">Selected-group what-if NCR</span><strong>{money(whatIfNcr)} <small>per EU</small></strong><span className={whatIfNcr !== null && currentMixNcr !== null && whatIfNcr >= currentMixNcr ? 'up' : 'down'}>{whatIfNcr !== null && currentMixNcr !== null ? `${whatIfNcr >= currentMixNcr ? '+' : '−'}${money(Math.abs(whatIfNcr - currentMixNcr)).replace('−', '')} vs current mix` : 'Select products with invoice volume'}</span></div>
            <p className="pc-muted">Mix inputs are relative weights within each CTA. Rebate assumptions are exploratory and do not alter the baseline scenario.</p>
            <Button onClick={useInScenario} disabled={selectedIds.size === 0}>Use in scenario<i className="pi pi-arrow-right" aria-hidden="true" /></Button>
          </aside>
        </div>
      )}
    </div>
  );
};

export default ProductComparison;
